// COPIED from InGo's console/src/maps/places/layer.ts by scripts/build-places.mjs,
// its types erased: do not edit here. Change the console's file and run
//   node scripts/build-places.mjs
// source sha256 30c9855410fd31badb91e6cacfadb9597626e8443a6a24f4b5a6a3702d258c52
// copy sha256 e8eebb3c8cb5726f6c8ce94dd90bf826212b99df6d18a0872481503e972d6a2e
import { PLACE_BADGES } from './badges'
import { PLACE_CATEGORIES, placeBadgeId,                    } from './categories'

/**
 * Places on the map, from the tiles every map already downloads (the
 * live-map plan; cut from KangaruRide `passenger-core/src/poi.ts` and the web
 * half `lib/map/poiLayer.ts`, ADR-0092 §3).
 *
 * The styling only: the expressions that pick the twenty kinds out of the
 * OpenMapTiles `poi` layer, and the one block of two symbol layers a map
 * adds. No tile provider, no key, no request.
 *
 * **Changed in the cut:** Kangaru's Positron had one source called `carto`
 * and drew no places of its own. InGo's style (OpenFreeMap's, and the
 * deployment's own later) names its source differently and draws a few
 * places from its own sprite, so the source is found in the style, and the
 * style's own place icons give way to these (otherwise every petrol station
 * would show twice). The badges sit **under the street names**, where the
 * style drew its own places, so a street keeps its name over a café.
 *
 * The map is typed on the few methods used, so the tests run without WebGL.
 *
 * **Two tile schemas** (the go-live scan, 3 Oct 2026). OpenFreeMap's tiles,
 * on the development machine, are OpenMapTiles: places in `poi` by
 * `class`/`subclass`, airports apart in `aerodrome_label`, street names in
 * `transportation_name`. The deployment's own tiles are Protomaps'
 * (`deploy/routing/tiles/make-style.mjs`): places and airports in `pois` by
 * `kind`, street names in `roads`. Read with the first schema, the second
 * drew no place at all, quietly. The schema is read from the style, so one
 * build serves both.
 */

/** The two layers this adds, by id. */
export const PLACES_LAYER_ID = 'ingo-places'
export const AIRPORTS_LAYER_ID = 'ingo-airports'
export const PLACE_LAYER_IDS = [PLACES_LAYER_ID, AIRPORTS_LAYER_ID]

/** The schema of the tiles a style draws. */

/** OpenMapTiles' usual source name, when a style names none that holds the tiles. */
const FALLBACK_SOURCE = 'openmaptiles'
/** Protomaps' names, as the deployment's style uses them. */
const PROTOMAPS = { source: 'protomaps', places: 'pois', roads: 'roads', placeNames: 'places' }
/** The face a style's own labels use is read at runtime; this when there is none. */
const FALLBACK_FONT = ['Noto Sans Regular']

/** A MapLibre expression, loosely. */

/** What a tap on a place hands back: the tile's name, its kind, where it is. */


/** The MapLibre methods used here, nothing more, so any `Map` fits and a test can fake one. */

/** The part of a MapLibre layer event a tap reads. */


/** `[class, subclass]` -> does a feature belong to this kind at this zoom? Class and subclass together, never subclass alone. */
function matchExpression(category               , atZoom        )                         {
  const pairs = category.matches.filter((m) => (m.minzoom ?? category.minzoom) <= atZoom)
  if (pairs.length === 0) return null
  return ['any', ...pairs.map((m) => ['all', ['==', ['get', 'class'], m.class], ['==', ['get', 'subclass'], m.subclass]])]
}

/** The same on Protomaps' tiles: is the feature's `kind` one of this kind's, at this zoom? */
function kindExpression(category               , atZoom        )                         {
  const kinds = category.kinds.filter((k) => (k.minzoom ?? category.minzoom) <= atZoom).map((k) => k.kind)
  if (kinds.length === 0) return null
  return ['in', ['get', 'kind'], ['literal', kinds]]
}

/** Every zoom at which some kind starts, ascending. */
function zoomGates(categories                          , schema            )           {
  const gates = new Set        ()
  for (const category of categories) {
    gates.add(category.minzoom)
    for (const m of schema === 'protomaps' ? category.kinds : category.matches) if (m.minzoom !== undefined) gates.add(m.minzoom)
  }
  return [...gates].sort((a, b) => a - b)
}

/**
 * The places layer's filter: a `step` on zoom whose every stop admits the
 * kinds whose zoom has come. A filter may read `zoom` only as the input of a
 * top-level `step`, which is why the gates are not written inside each match.
 */
export function placeFilter(categories                           = PLACE_CATEGORIES, schema             = 'openmaptiles')                  {
  const tiled = categories.filter((c) => c.layer === 'poi')
  const expression = schema === 'protomaps' ? kindExpression : matchExpression
  const step            = ['step', ['zoom'], false]
  for (const gate of zoomGates(tiled, schema)) {
    const admitted = tiled.map((c) => expression(c, gate)).filter((e)                       => e !== null)
    step.push(gate, admitted.length === 0 ? false : ['any', ...admitted])
  }
  return step
}

/** `subclass` (or, on Protomaps' tiles, `kind`) -> the badge's image id. The filter has already limited them. */
export function placeIconExpression(categories                           = PLACE_CATEGORIES, schema             = 'openmaptiles')                  {
  const branches            = []
  for (const category of categories) {
    if (category.layer !== 'poi') continue
    const values = [...new Set(schema === 'protomaps' ? category.kinds.map((k) => k.kind) : category.matches.map((m) => m.subclass))]
    branches.push(values.length === 1 ? values[0] : values, placeBadgeId(category.id))
  }
  return ['match', ['get', schema === 'protomaps' ? 'kind' : 'subclass'], ...branches, '']
}

/** The same in reverse, for a tap: which kind does a feature's subclass name? */
export function placeCategoryOf(subclass         )                {
  if (typeof subclass !== 'string') return null
  return PLACE_CATEGORIES.find((c) => c.matches.some((m) => m.subclass === subclass))?.id ?? null
}

/** The same for a feature on Protomaps' tiles, by its `kind` (the airport's included). */
export function placeCategoryOfKind(kind         )                {
  if (typeof kind !== 'string') return null
  return PLACE_CATEGORIES.find((c) => c.kinds.some((k) => k.kind === kind))?.id ?? null
}

/** Which schema the style's tiles carry: Protomaps' when any layer reads `pois`. */
export function tileSchema(map                            )             {
  return (map.getStyle()?.layers ?? []).some((l) => l['source-layer'] === PROTOMAPS.places) ? 'protomaps' : 'openmaptiles'
}

/** A kind's own words, for the line under a place's name; null for one the table does not know. */
export function placeKindLabel(category               )                {
  if (category === null) return null
  return PLACE_CATEGORIES.find((c) => c.id === category)?.label ?? null
}

/** The vector source holding the OpenMapTiles layers: the one the style's own places, or roads, are drawn from. */
export function tileSource(map          )         {
  const layers = map.getStyle()?.layers ?? []
  const of = (sourceLayer        ) => layers.find((l) => l['source-layer'] === sourceLayer && typeof l.source === 'string')?.source
  if (tileSchema(map) === 'protomaps') return of(PROTOMAPS.places) ?? of(PROTOMAPS.roads) ?? PROTOMAPS.source
  return of('poi') ?? of('transportation') ?? FALLBACK_SOURCE
}

/** Where the block goes: under the street names, else under the place names, else on top. */
export function placesBeforeId(map          )                     {
  const layers = map.getStyle()?.layers ?? []
  if (tileSchema(map) === 'protomaps') {
    // A road layer that writes a name: Protomaps' first symbol layer on the
    // roads is the one-way arrows, which carry no text and no font.
    const streetNames = layers.find((l) => l.type === 'symbol' && l['source-layer'] === PROTOMAPS.roads && l.layout?.['text-font'] !== undefined)
    return (streetNames ?? layers.find((l) => l['source-layer'] === PROTOMAPS.placeNames))?.id
  }
  return (layers.find((l) => l['source-layer'] === 'transportation_name') ?? layers.find((l) => l['source-layer'] === 'place'))?.id
}

function textFontOf(map          , layerId                    )           {
  if (layerId === undefined) return FALLBACK_FONT
  const font = map.getStyle()?.layers?.find((l) => l.id === layerId)?.layout?.['text-font']
  return Array.isArray(font) && font.length > 0 && font.every((f) => typeof f === 'string') ? (font            ) : FALLBACK_FONT
}

function symbolLayer(
  id        ,
  source        ,
  sourceLayer                                                     ,
  minzoom        ,
  filter                 ,
  icon                          ,
  font          ,
  options                  ,
)                          {
  return {
    id,
    type: 'symbol',
    source,
    'source-layer': sourceLayer,
    minzoom,
    filter,
    layout: {
      'icon-image': icon,
      'icon-size': 1,
      // The map thins itself: a badge that would sit on another is dropped,
      // and its name is optional, so a badge still shows where its name does
      // not fit. Density is this layer's risk (ADR-0092).
      'icon-allow-overlap': false,
      'text-field': ['get', 'name'],
      'text-font': font,
      'text-size': 11,
      'text-anchor': 'top',
      'text-offset': [0, 1.2],
      'text-max-width': 8,
      'text-optional': true,
      visibility: options.visible ? 'visible' : 'none',
    },
    paint: { 'text-color': options.text, 'text-halo-color': options.halo, 'text-halo-width': 1.2 },
  }
}

/** An `<img>` decodes SVG everywhere; MapLibre's own loader does not in Chrome (crbug 606319). */
export function loadSvgImage(dataUri        )                            {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('a place badge could not be decoded'))
    image.src = dataUri
  })
}

/**
 * Register every badge and add the two layers, once the style has loaded.
 * Once per map: a second call on a map that has the block does nothing.
 */
export async function addPlaces(map          , options                  )                {
  if (map.getLayer(PLACES_LAYER_ID) !== undefined) return
  const load = options.loadImage ?? loadSvgImage

  await Promise.all(
    Object.entries(PLACE_BADGES).map(async ([id, uri]) => {
      const badge = placeBadgeId(id)
      if (map.hasImage(badge)) return
      map.addImage(badge, await load(uri), { pixelRatio: 2 })
    }),
  )

  // The badges loaded while the map was being taken down, or a second call won the race.
  if (map.getLayer(PLACES_LAYER_ID) !== undefined) return

  const layers = map.getStyle()?.layers ?? []
  const schema = tileSchema(map)
  const source = tileSource(map)
  const before = placesBeforeId(map)
  const font = textFontOf(map, before)

  // The style's own places give way to these, or each would show twice.
  for (const layer of layers) {
    if (layer.type === 'symbol' && (layer['source-layer'] === 'poi' || layer['source-layer'] === 'aerodrome_label')) map.removeLayer(layer.id)
    if (layer.type === 'symbol' && layer['source-layer'] === PROTOMAPS.places) map.removeLayer(layer.id)
  }

  const tiled = PLACE_CATEGORIES.filter((c) => c.layer === 'poi')
  const airport = PLACE_CATEGORIES.find((c) => c.layer === 'aerodrome_label')
  const placesLayer = schema === 'protomaps' ? PROTOMAPS.places : 'poi'
  map.addLayer(symbolLayer(PLACES_LAYER_ID, source, placesLayer, Math.min(...tiled.map((c) => c.minzoom)), placeFilter(PLACE_CATEGORIES, schema), placeIconExpression(PLACE_CATEGORIES, schema), font, options), before)
  if (airport !== undefined) {
    // OpenMapTiles keeps airports in their own layer; Protomaps keeps them among the places, by kind.
    const airports                                                            =
      schema === 'protomaps'
        ? [PROTOMAPS.places, ['all', ['has', 'name'], ['in', ['get', 'kind'], ['literal', airport.kinds.map((k) => k.kind)]]]]
        : ['aerodrome_label', ['has', 'name']]
    map.addLayer(symbolLayer(AIRPORTS_LAYER_ID, source, airports[0], airport.minzoom, airports[1], placeBadgeId(airport.id), font, options), before)
  }

  if (options.onTap === undefined) return
  const onTap = options.onTap

  for (const id of PLACE_LAYER_IDS) {
    map.on('click', id, (event) => {
      const feature = event.features?.[0]
      const name = feature?.properties?.name
      if (typeof name !== 'string' || name === '') return
      // The place's own point, not the tap: a badge is 24 px wide and the tap was on its edge.
      const coordinates = feature?.geometry?.type === 'Point' ? feature.geometry.coordinates : undefined
      const [lng, lat] =
        Array.isArray(coordinates) && typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number'
          ? [coordinates[0], coordinates[1]]
          : [event.lngLat.lng, event.lngLat.lat]
      const properties = feature?.properties
      const category = schema === 'protomaps' ? placeCategoryOfKind(properties?.kind) : placeCategoryOf(properties?.subclass)
      onTap({ name, category: id === AIRPORTS_LAYER_ID ? 'airport' : category, lng, lat })
    })
    map.on('mouseenter', id, () => {
      map.getCanvas().style.cursor = 'pointer'
    })
    map.on('mouseleave', id, () => {
      map.getCanvas().style.cursor = ''
    })
  }
}

/** Show or hide both layers; a map without the block is left alone. */
export function setPlacesVisible(map                                                  , visible         )       {
  for (const id of PLACE_LAYER_IDS) {
    if (map.getLayer(id) === undefined) continue
    map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none')
  }
}

/**
 * What a tapped place says for itself, for a MapLibre popup: its name, and
 * its kind under it (Kangaru's `placeCard`, without the order map's actions:
 * the office's map offers none). Built with the DOM, never markup: the name is
 * the tile's text, and a place called `<img onerror=…>` must stay text.
 */
export function placeCard(tap          )                 {
  const card = document.createElement('div')
  card.className = 'ingo-place'

  const name = document.createElement('p')
  name.className = 'ingo-place__name'
  name.textContent = tap.name
  card.append(name)

  const kind = placeKindLabel(tap.category)
  if (kind !== null) {
    const line = document.createElement('p')
    line.className = 'ingo-place__kind'
    line.textContent = kind
    card.append(line)
  }

  return card
}
